import { Box, Checkbox, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, useTheme } from "@mui/material";
import Text from "../../VehicleSales/Text";
import { Delete, Info, InfoOutlined, LocalHospital } from "@mui/icons-material";
import formatCurrency from "../../../system/formatCurrency";
import CurrencyInput from "../../../Components/CurrencyInput";
import PercentageInput from "../../../Components/PercentageInput";
import { AssessmentDamageListElement, BookingCode, ThemeColor, Validation } from "../../../system/Domain";
import { Dispatch, SetStateAction } from "react";
import { effectiveFactor, isManualGoodwill } from "./damageCalculation";
import { ValidationDeductionCalculationType } from "../RemarketingLayout";

interface Props {
	damageList: AssessmentDamageListElement[]
	discountValue: number
	bookingCodes: BookingCode[]
	hasMechanicalService: boolean
	hasTireService: boolean
	hasInsurance: boolean
	setValidation: Dispatch<SetStateAction<Validation>>
	disabled: boolean
	contractEndDate?: string | null
	calculationType: ValidationDeductionCalculationType
}

const AssessmentDamagesTable = (props: Props) => {
	const theme = useTheme();
	const discountFactor = props.discountValue / 100;

	const getTextColor = (d: AssessmentDamageListElement): ThemeColor => {
		if (d.bookingCode?.bookingKey === "RS07" || d.bookingCode?.bookingKey === "RU14")
			return "warning";

		if (d.bookingCode?.bookingKey === "RS01")
			return "primary";

		return null;
	};

	return (
		<Table>
			<colgroup>
				<col width="auto" />
				<col width="280px" />
				<col width="0" />
				<col width="0" />
				<col width="100px" />
				<col width="0" />
				<col width="50px" />
				<col width="0" />
			</colgroup>
			<TableHead>
				<TableRow>
					<TableCell>Schaden</TableCell>
					<TableCell></TableCell>
					<TableCell></TableCell>
					<TableCell>Reparatur</TableCell>
					<TableCell sx={{ textAlign: "right" }}>Belastung</TableCell>
					<TableCell>
						<Checkbox
							checked={props.damageList?.every(i => i.factor === discountFactor) || false}
							disabled={props.disabled}
							onClick={() => {
								const allFactor = props.damageList?.every(i => i.factor === 1);
								props.setValidation(prev => ({
									...prev,
									damages: prev.damages.map(r => ({
										...r,
										factor: allFactor ? discountFactor : 1,
										partialDepreciation: !r.partialDepreciation
									}))
								}));
							}}
						/>
					</TableCell>
					<TableCell>Minderwert</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{props.damageList?.map((d, i) =>
					<TableRow key={i} hover>
						<TableCell>
							{/*Schaden*/}
							{!d.isNew && (
								<Text
									value={d.text}
									color={getTextColor(d)}
								/>
							)}
							{d.isNew && (
								<Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
									<IconButton
										disabled={props.disabled}
										onClick={() =>
											props.setValidation(prev => ({
												...prev,
												damages: prev.damages.map((item, index) => i === index && item.isNew ? null : item)
													.filter(item => item !== null)
											}))
										}
									>
										<Delete />
									</IconButton>
									<Box ml={2} sx={{ width: "100%" }}>
										<TextField
											value={d.text}
											variant="standard"
											fullWidth
											disabled={props.disabled}
											onChange={(e) => {
												const newName = e.target.value;
												props.setValidation(prev => ({
													...prev,
													damages: prev.damages.map((item, index) =>
														i === index ? { ...item, text: newName } : item
													)
												}));
											}}
										/>
									</Box>
								</Box>
							)}
						</TableCell>
						<TableCell>
							<Select
								fullWidth
								variant="standard"
								value={d.bookingCode.key}
								disabled={props.disabled || !props.contractEndDate}
								onChange={(e) => {
									const selectedCode = props.bookingCodes.find(f => f.key === e.target.value);
									props.setValidation(prev => ({
										...prev,
										damages: prev.damages.map((item, index) =>
											i === index ? { ...item, bookingCode: selectedCode!, ...(item.isNew && selectedCode?.key === "RS06" ? { factor: 1, partialDepreciation: false } : {}) } : item
										)
									}));
								}}
							>
								{props.bookingCodes
									.filter(bc => {
										if (bc.key === 'RS02' && !props.hasMechanicalService) return false;
										if (bc.key === 'RS03' && !props.hasTireService) return false;
										if (bc.key === "RS01-1" && !props.hasInsurance) return false;
										if (bc.key === "RS01" && !props.hasInsurance) return false; // Glasschaden mit Versicherung
										if (bc.key === "RS07" && props.hasInsurance) return false; // Glasschaden ohne Versicherung
										if (!d.isNew && bc.key === "RS05") return false;
										return true;
									})
									.sort((a, b) => a.label.localeCompare(b.label))
									.map((bc, i) => (
										<MenuItem value={bc.key} key={i}>{bc.label}</MenuItem>
									))}
							</Select>
						</TableCell>
						<TableCell>
							{d.bookingCode && d.bookingCode.info && (
								<Tooltip
									title={
										<Box>
											{d.bookingCode?.info}
										</Box>
									}
								>
									<Info />
								</Tooltip>
							)}
							{(!d.bookingCode || !d.bookingCode.info) && (
								<InfoOutlined />
							)}
						</TableCell>
						<TableCell sx={{ textAlign: "right" }}>
							{/*Reparatur*/}
							{!d.isNew && (
								<>{formatCurrency(d.netAmount * d.bookingCode.factor)}</>
							)}
							{d.isNew && (
								<CurrencyInput
									fullWidth
									value={d.netAmount * d.bookingCode.factor}
									disabled={props.disabled}
									setValue={(e: number) =>
										props.setValidation(prev => ({
											...prev,
											damages: prev.damages.map((item, index) =>
												i === index ? { ...item, netAmount: e } : item
											)
										}))
									}
									variant="standard"
								/>
							)}
						</TableCell>
						<TableCell sx={{ textAlign: "right" }}>
							{/*Belastung*/}
							{!d.isNew && (
								<Box>
									{!d.useAssessmentNetValues && (
										<Box>{Math.round(d.factor * 100)}%</Box>
									)}
									{d.useAssessmentNetValues && (
										<Box
											title="Vorhandene Werte des Gutachtens werden verwendet."
											sx={{ color: theme.palette.secondary.main }}
										>{Math.round(Math.abs(d.depreciationAmountNet) / d.netAmount * 100 * d.bookingCode.factor)}%</Box>
									)}
									{d.factor !== (d.partialDepreciation ? discountFactor : 1) && (
										<Box sx={{ fontSize: "0.7rem", textDecoration: "line-through" }}>{d.partialDepreciation ? props.discountValue : 100}%</Box>
									)}
								</Box>
							)}
							{d.isNew && (
								<PercentageInput
									fullWidth
									value={Math.round(effectiveFactor(d) * 100)}
									disabled={props.disabled || isManualGoodwill(d)}
									setValue={(e: number) =>
										props.setValidation(prev => ({
											...prev,
											damages: prev.damages.map((item, index) =>
												i === index ? { ...item, factor: e / 100 } : item
											)
										}))
									}
									variant="standard"
								/>
							)}
						</TableCell>
						<TableCell>
							<Checkbox
								checked={d.factor !== 1}
								disabled={d.useAssessmentNetValues || isManualGoodwill(d) || props.disabled}
								onChange={() => {
									props.setValidation(prev => ({
										...prev,
										damages: prev.damages.map((item, index) => {
											if (i === index) {
												return {
													...item,
													factor: item.factor === 1 ? discountFactor : 1,
													partialDepreciation: !item.partialDepreciation
												};
											}
											return item;
										}),
									}));
								}}
							/>
						</TableCell>
						<TableCell sx={{ textAlign: "right" }}>
							{/*Minderwert*/}
							{d.useAssessmentNetValues && (
								<Box>{formatCurrency(Math.abs(d.depreciationAmountNet) * d.bookingCode.factor)}</Box>
							)}
							{!d.useAssessmentNetValues && (
								<Box>
									<Box>{formatCurrency(d.netAmount * effectiveFactor(d) * d.bookingCode.factor)}</Box>
									{effectiveFactor(d) !== (d.partialDepreciation ? discountFactor : 1) && (
										<Box sx={{
											fontSize: "0.7rem",
											textDecoration: "line-through"
										}}>{formatCurrency(d.netAmount * (d.partialDepreciation ? discountFactor : 1) * d.bookingCode.factor)}</Box>
									)}
								</Box>
							)}
						</TableCell>
					</TableRow>
				)}
				{props.damageList?.length === 0 && (
					<TableRow>
						<TableCell colSpan={7} sx={{ textAlign: "center" }}>
							Keine Daten gefunden
						</TableCell>
					</TableRow>
				)}
				<TableRow hover>
					<TableCell colSpan={7}>
						<Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
							<IconButton
								disabled={props.disabled}
								onClick={() => {
									const newItem: AssessmentDamageListElement = {
										bookingCode: props.bookingCodes.find(f => f.key === "RU02-1"),
										factor: 1,
										isNew: true,
										text: "",
										netAmount: null,
										useAssessmentNetValues: false,
										type: "Damage",
										partialDepreciation: false
									};

									props.setValidation(prev => ({
										...prev,
										damages: [...prev.damages, newItem]
									}));
								}}
							>
								<LocalHospital />
							</IconButton>

							<Box ml={2}>
								Position oder Freibetrag manuell hinzufügen
							</Box>
						</Box>
					</TableCell>
				</TableRow>
			</TableBody>
		</Table>
	);
};

export default AssessmentDamagesTable;
